import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageShell from '@/components/Layout/PageShell';
import { getErrorDetail } from '@/utils/apiError';
import {
  useGetPlatformTenantQuery,
  useUpdatePlatformTenantLifecycleMutation,
  useUpdatePlatformTenantSubscriptionMutation,
} from '@/redux/internal-services/platformAdminApi';
import { useListPlatformPlansQuery } from '@/redux/internal-services/platformAdminApi';

type Tab = 'overview' | 'lifecycle' | 'subscription';

const STATUS_BADGE: Record<string, string> = {
  Active:      'bg-green-100 text-green-800',
  Trial:       'bg-blue-100 text-blue-800',
  Suspended:   'bg-yellow-100 text-yellow-800',
  Deactivated: 'bg-red-100 text-red-800',
};

const ALLOWED_TRANSITIONS: Record<string, string[]> = {
  Active:      ['Suspended', 'Deactivated'],
  Trial:       ['Active', 'Suspended', 'Deactivated'],
  Suspended:   ['Active', 'Deactivated'],
  Deactivated: ['Active'],
};

const TenantDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('overview');

  const { data, isLoading, refetch } = useGetPlatformTenantQuery(tenantId!, { skip: !tenantId });
  const { data: plansData } = useListPlatformPlansQuery();
  const [updateLifecycle, { isLoading: savingLifecycle }] = useUpdatePlatformTenantLifecycleMutation();
  const [updateSubscription, { isLoading: savingSub }] = useUpdatePlatformTenantSubscriptionMutation();

  const tenant = data?.data;
  const plans = plansData?.data ?? [];

  const [newStatus, setNewStatus] = useState('');
  const [reason, setReason] = useState('');
  const [lifecycleMsg, setLifecycleMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [subMsg, setSubMsg] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  const handleLifecycleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setLifecycleMsg(null);
    if (!newStatus) { setLifecycleMsg({ type: 'error', text: 'Select a target status.' }); return; }
    if (!reason.trim()) { setLifecycleMsg({ type: 'error', text: 'Reason is required.' }); return; }
    try {
      await updateLifecycle({ tenantId: tenantId!, status: newStatus, reason: reason.trim() }).unwrap();
      setLifecycleMsg({ type: 'success', text: 'Status updated successfully.' });
      setNewStatus('');
      setReason('');
      refetch();
    } catch (err: unknown) {
      setLifecycleMsg({ type: 'error', text: getErrorDetail(err) ?? 'Failed to update status.' });
    }
  };

  const handleSubscriptionUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubMsg(null);
    if (!selectedPlanId) { setSubMsg({ type: 'error', text: 'Select a plan.' }); return; }
    try {
      await updateSubscription({ tenantId: tenantId!, plan_id: selectedPlanId, billing_cycle: billingCycle }).unwrap();
      setSubMsg({ type: 'success', text: 'Subscription updated.' });
      refetch();
    } catch (err: unknown) {
      setSubMsg({ type: 'error', text: getErrorDetail(err) ?? 'Failed to update subscription.' });
    }
  };

  if (isLoading) {
    return <div className="flex items-center justify-center h-64 text-ui-text3">Loading tenant...</div>;
  }
  if (!tenant) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3">
        <p className="text-ui-text3">{t('platform.tenantNotFound')}</p>
        <button onClick={() => navigate('/platform/tenants')} className="text-sm text-brand-dark hover:underline">← Back to tenants</button>
      </div>
    );
  }

  const currentStatus = tenant.lifecycle_status;
  const allowedNext = ALLOWED_TRANSITIONS[currentStatus] ?? [];
  const history = tenant.lifecycle_history ?? [];

  const TABS: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'lifecycle', label: 'Lifecycle' },
    { key: 'subscription', label: 'Subscription' },
  ];

  return (
    <PageShell
      maxWidth="max-w-5xl"
      header={
        <>
          <button
            onClick={() => navigate('/platform/tenants')}
            className="flex items-center gap-1.5 text-sm text-ui-text2 hover:text-brand-dark mb-3 transition"
          >
            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            All Tenants
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
            <div>
              <h1 className="text-2xl font-bold text-ui-text">{tenant.company_name}</h1>
              {tenant.primary_contact?.email && (
                <p className="text-sm text-ui-text2 mt-0.5">{tenant.primary_contact.email}</p>
              )}
            </div>
            <span className={`self-start sm:self-auto inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${STATUS_BADGE[currentStatus] ?? 'bg-ui-subtle text-ui-text'}`}>
              {currentStatus}
            </span>
          </div>
          <div className="flex gap-0 border-b border-ui-border overflow-x-auto -mb-4">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`px-5 py-3 text-sm font-medium whitespace-nowrap transition border-b-2 -mb-px ${
                  activeTab === tab.key
                    ? 'text-brand-dark border-brand-dark'
                    : 'text-ui-text2 border-transparent hover:text-ui-text hover:border-ui-border'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </>
      }
    >

      {/* ── Overview Tab ───────────────────────────────────────────────── */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-ui-card border border-ui-border2 rounded-xl p-6 shadow-sm">
            <h3 className="text-sm font-semibold text-ui-text2 uppercase tracking-wide mb-4">Company Info</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-ui-text3">Company Name</dt>
                <dd className="text-sm font-medium text-ui-text">{tenant.company_name}</dd>
              </div>
              {tenant.primary_contact?.name && (
                <div>
                  <dt className="text-xs text-ui-text3">Primary Contact</dt>
                  <dd className="text-sm text-ui-text">{tenant.primary_contact.name}</dd>
                </div>
              )}
              {tenant.primary_contact?.email && (
                <div>
                  <dt className="text-xs text-ui-text3">Email</dt>
                  <dd className="text-sm text-ui-text">{tenant.primary_contact.email}</dd>
                </div>
              )}
              {tenant.primary_contact?.phone && (
                <div>
                  <dt className="text-xs text-ui-text3">Phone</dt>
                  <dd className="text-sm text-ui-text">{tenant.primary_contact.phone}</dd>
                </div>
              )}
              {tenant.created_at && (
                <div>
                  <dt className="text-xs text-ui-text3">Joined</dt>
                  <dd className="text-sm text-ui-text">{new Date(tenant.created_at).toLocaleDateString()}</dd>
                </div>
              )}
            </dl>
          </div>

          <div className="bg-ui-card border border-ui-border2 rounded-xl p-6 shadow-sm">
            <h3 className="text-sm font-semibold text-ui-text2 uppercase tracking-wide mb-4">Subscription</h3>
            <dl className="space-y-3">
              <div>
                <dt className="text-xs text-ui-text3">Plan</dt>
                <dd className="text-sm font-medium text-ui-text">{tenant.plan_name ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-ui-text3">Billing Cycle</dt>
                <dd className="text-sm text-ui-text">{tenant.subscription?.billing_cycle ?? '—'}</dd>
              </div>
              <div>
                <dt className="text-xs text-ui-text3">Subscription Status</dt>
                <dd className="text-sm text-ui-text">{tenant.subscription?.status ?? '—'}</dd>
              </div>
              {tenant.subscription?.trial_end && (
                <div>
                  <dt className="text-xs text-ui-text3">Trial Ends</dt>
                  <dd className="text-sm text-ui-text">{new Date(tenant.subscription.trial_end).toLocaleDateString()}</dd>
                </div>
              )}
            </dl>
          </div>

          {tenant.address && (
            <div className="bg-ui-card border border-ui-border2 rounded-xl p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-ui-text2 uppercase tracking-wide mb-4">Address</h3>
              <p className="text-sm text-ui-text">
                {[tenant.address.street, tenant.address.city, tenant.address.state, tenant.address.zip, tenant.address.country]
                  .filter(Boolean).join(', ') || '—'}
              </p>
            </div>
          )}

          {(tenant.scope_of_work ?? []).length > 0 && (
            <div className="bg-ui-card border border-ui-border2 rounded-xl p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-ui-text2 uppercase tracking-wide mb-4">Scope of Work</h3>
              <div className="flex flex-wrap gap-2">
                {tenant.scope_of_work!.map((s) => (
                  <span key={s} className="px-2.5 py-1 bg-brand-light text-brand-dark text-xs font-medium rounded-full">{s}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Lifecycle Tab ──────────────────────────────────────────────── */}
      {activeTab === 'lifecycle' && (
        <div className="space-y-6">
          <div className="bg-ui-card border border-ui-border2 rounded-xl p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <span className="text-sm font-medium text-ui-text2">Current Status:</span>
              <span className={`px-3 py-1 rounded-full text-sm font-semibold ${STATUS_BADGE[currentStatus] ?? 'bg-ui-subtle text-ui-text'}`}>
                {currentStatus}
              </span>
            </div>

            {allowedNext.length > 0 ? (
              <form onSubmit={handleLifecycleUpdate} className="space-y-4 max-w-md">
                <div>
                  <label className="block text-sm font-medium text-ui-text mb-1">Transition To</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
                  >
                    <option value="">Select status...</option>
                    {allowedNext.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ui-text mb-1">Reason <span className="text-red-500">*</span></label>
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40 resize-none"
                    placeholder="Explain the reason for this transition..."
                  />
                </div>
                {lifecycleMsg && (
                  <p className={`text-sm ${lifecycleMsg.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>
                    {lifecycleMsg.text}
                  </p>
                )}
                <button
                  type="submit"
                  disabled={savingLifecycle}
                  className="bg-brand-dark text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#3d2aa0] transition disabled:opacity-50"
                >
                  {savingLifecycle ? 'Applying...' : 'Apply Transition'}
                </button>
              </form>
            ) : (
              <p className="text-sm text-ui-text2">No transitions available from current status.</p>
            )}
          </div>

          {history.length > 0 && (
            <div className="bg-ui-card border border-ui-border2 rounded-xl p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-ui-text2 uppercase tracking-wide mb-4">Transition History</h3>
              <div className="space-y-2">
                {[...history].reverse().map((entry, i) => (
                  <div key={i} className="flex items-start gap-3 bg-ui-raised rounded-lg p-3 text-sm">
                    <div className="flex-1">
                      <span className="font-medium text-ui-text">{entry.from}</span>
                      <span className="mx-2 text-ui-text3">→</span>
                      <span className="font-medium text-ui-text">{entry.to}</span>
                      {entry.reason && <p className="text-ui-text2 text-xs mt-1">{entry.reason}</p>}
                    </div>
                    <span className="text-ui-text3 whitespace-nowrap text-xs">
                      {entry.timestamp ? new Date(entry.timestamp).toLocaleDateString() : ''}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Subscription Tab ───────────────────────────────────────────── */}
      {activeTab === 'subscription' && (
        <div className="space-y-6">
          {/* Current subscription card */}
          <div className="bg-ui-card border border-ui-border2 rounded-xl p-6 shadow-sm">
            <h3 className="text-sm font-semibold text-ui-text2 uppercase tracking-wide mb-4">Current Subscription</h3>
            {tenant.subscription ? (
              <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Plan', value: tenant.subscription.plan_name ?? tenant.subscription.plan_id },
                  { label: 'Status', value: tenant.subscription.status },
                  { label: 'Billing Cycle', value: tenant.subscription.billing_cycle },
                  { label: 'Trial Start', value: tenant.subscription.trial_start ? new Date(tenant.subscription.trial_start).toLocaleDateString() : '—' },
                  { label: 'Trial End', value: tenant.subscription.trial_end ? new Date(tenant.subscription.trial_end).toLocaleDateString() : '—' },
                  { label: 'Billing Start', value: tenant.subscription.billing_start ? new Date(tenant.subscription.billing_start).toLocaleDateString() : '—' },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <dt className="text-xs text-ui-text3">{label}</dt>
                    <dd className="text-sm font-medium text-ui-text mt-0.5">{value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="text-sm text-ui-text3">No active subscription.</p>
            )}
          </div>

          {/* Change plan */}
          <div className="bg-ui-card border border-ui-border2 rounded-xl p-6 shadow-sm">
            <h3 className="text-sm font-semibold text-ui-text2 uppercase tracking-wide mb-4">Change Plan</h3>
            <form onSubmit={handleSubscriptionUpdate} className="space-y-4 max-w-md">
              <div>
                <label className="block text-sm font-medium text-ui-text mb-1">Plan</label>
                <select
                  value={selectedPlanId}
                  onChange={(e) => setSelectedPlanId(e.target.value)}
                  className="w-full border border-ui-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-dark/40"
                >
                  <option value="">Select plan...</option>
                  {plans.map((p) => (
                    <option key={p.plan_id} value={p.plan_id}>{p.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-ui-text mb-1">Billing Cycle</label>
                <div className="flex gap-3">
                  {['monthly', 'annual'].map((cycle) => (
                    <label key={cycle} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="billing_cycle"
                        value={cycle}
                        checked={billingCycle === cycle}
                        onChange={() => setBillingCycle(cycle)}
                        className="accent-brand-dark"
                      />
                      <span className="text-sm text-ui-text capitalize">{cycle}</span>
                    </label>
                  ))}
                </div>
              </div>
              {subMsg && (
                <p className={`text-sm ${subMsg.type === 'error' ? 'text-red-500' : 'text-green-600'}`}>
                  {subMsg.text}
                </p>
              )}
              <button
                type="submit"
                disabled={savingSub}
                className="bg-brand-dark text-white px-6 py-2 rounded-lg text-sm font-medium hover:bg-[#3d2aa0] transition disabled:opacity-50"
              >
                {savingSub ? 'Updating...' : 'Update Subscription'}
              </button>
            </form>
          </div>
        </div>
      )}
    </PageShell>
  );
};

export default TenantDetailPage;
